import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import AuthContext from '../../store/auth-context';
import { completeOnboarding } from '../../services/api/onboardingService';
import {
    completePolicyDocument,
    createDepartmentPolicy,
    deletePolicy,
    getManagedPolicies,
} from '../../services/api/policyService';
import { POLICY_SCHEMA_KEY } from '../department-policies/constants';
import DocxPolicyUpload, {
    policyListItemToParsed,
    type ParsedPolicy,
} from '../department-policies/upload/DocxPolicyUpload';

export default function PoliciesStep() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { toast } = useToast();
    const authContext = useContext(AuthContext);
    const [parsedPolicies, setParsedPolicies] = useState<ParsedPolicy[]>([]);

    useEffect(() => {
        let cancelled = false;
        const loadPolicies = async () => {
            try {
                const result = await getManagedPolicies({
                    tab: 'completed',
                    limit: 100,
                });
                if (cancelled) return;
                const existing = result.data.map(policyListItemToParsed);
                setParsedPolicies((prev) => {
                    const knownIds = new Set(
                        prev.map((p) => p._id).filter(Boolean)
                    );
                    const missing = existing.filter(
                        (p) => !knownIds.has(p._id)
                    );
                    return [...prev, ...missing];
                });
            } catch {
                // Silent — policies section will just show what's in local state
            }
        };
        loadPolicies();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAddPolicy = async (policy: ParsedPolicy): Promise<void> => {
        const created = await createDepartmentPolicy({
            schemaKey: POLICY_SCHEMA_KEY,
            effectiveDate: new Date(policy.effectiveDate).toISOString(),
            expiryDate: new Date(policy.expiryDate).toISOString(),
            tags: policy.tags,
        });
        await completePolicyDocument(
            created._id,
            { policyData: policy.html },
            policy.title,
            t('onboarding.policies.importedFromDocx')
        );
        setParsedPolicies((prev) => [...prev, { ...policy, _id: created._id }]);
    };

    const handleUpdatePolicy = (updated: ParsedPolicy) => {
        setParsedPolicies((prev) =>
            prev.map((p) => (p._id === updated._id ? updated : p))
        );
    };

    const handleRemovePolicy = async (policy: ParsedPolicy): Promise<void> => {
        if (policy._id) {
            await deletePolicy(policy._id);
        }
        setParsedPolicies((prev) => prev.filter((p) => p !== policy));
    };

    const handleComplete = async () => {
        try {
            const result = await completeOnboarding();
            if (!result?.success) {
                toast({
                    title: t('onboarding.policies.toasts.loadError'),
                    variant: 'destructive',
                });
                return;
            }
            authContext.markOnboardingCompleted();
            window.location.assign('/');
        } catch {
            toast({
                title: t('onboarding.policies.toasts.loadError'),
                variant: 'destructive',
            });
        }
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-lg font-semibold text-foreground">
                    {t('onboarding.policies.title')}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                    {t('onboarding.policies.description')}
                </p>
            </div>

            <DocxPolicyUpload
                policies={parsedPolicies}
                onAdd={handleAddPolicy}
                onRemove={handleRemovePolicy}
                onUpdate={handleUpdatePolicy}
            />

            <div className="flex justify-between pt-4">
                <Button
                    variant="outline"
                    onClick={() => navigate('/wizard/scanners')}
                >
                    {t('onboarding.common.back')}
                </Button>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={handleComplete}>
                        {t('onboarding.common.skipForNow')}
                    </Button>
                    <Button onClick={handleComplete}>
                        {t('onboarding.policies.completeSetup')}
                    </Button>
                </div>
            </div>
        </div>
    );
}
